import type {
  ApplicationIndex,
  ConversationIndex,
} from '@/core/conversations/conversation-index/conversation-index.type';
import { JobApplicantStatus, JobStatus } from '@factoryfixinc/ats-interfaces';

export interface ApplicationDisplay {
  primaryApplication: string; // "[1234] CNC Machine Operator, Jeffersonville, IN" or "—"
  additionalCount: string; // "+2 more" or ""
}

export class ApplicationDisplayUtil {
  /**
   * Returns archive-eligible applications: excludes NOTIFIED, NOT_INTERESTED,
   * and jobs with JobStatus.REMOVED.
   */
  static getValidArchiveApplications(conversationIndex: ConversationIndex): ApplicationIndex[] {
    const applicationIndexes = conversationIndex.applicationIndexes ?? [];

    return applicationIndexes.filter(
      (app) =>
        app.applicationStatus !== JobApplicantStatus.NOTIFIED &&
        app.applicationStatus !== JobApplicantStatus.NOT_INTERESTED &&
        app.jobStatus !== JobStatus.REMOVED,
    );
  }

  /**
   * Returns the most recent valid archive application, or null if none exist.
   * Used by both display (formatApplications) and navigation (CandidateList).
   */
  static getPrimaryApplication(conversationIndex: ConversationIndex): ApplicationIndex | null {
    const validApplications = this.getValidArchiveApplications(conversationIndex);

    if (validApplications.length === 0) {
      return null;
    }

    return validApplications.reduce((latest, current) => {
      const latestTs = new Date(latest.applicationApplyTs || 0);
      const currentTs = new Date(current.applicationApplyTs || 0);
      return currentTs > latestTs ? current : latest;
    });
  }

  /**
   * Formats application data for the Applications column in Candidate Archive.
   */
  static formatApplications(conversationIndex: ConversationIndex): ApplicationDisplay {
    const validApplications = this.getValidArchiveApplications(conversationIndex);
    const primary = this.getPrimaryApplication(conversationIndex);

    if (!primary) {
      return {
        primaryApplication: '—',
        additionalCount: '',
      };
    }

    const jobDisplayTitle = primary.jobDisplayTitle || 'Unknown Position';
    const jobLocation = primary.locationString || 'Unspecified Location';
    const primaryApplication = `[${primary.jobId}] ${jobDisplayTitle}, ${jobLocation}`;

    const additionalCount =
      validApplications.length > 1 ? `+${validApplications.length - 1} more` : '';

    return {
      primaryApplication,
      additionalCount,
    };
  }
}
